import React, { useEffect, useState, useCallback } from 'react'
import PrivateLayout from '../../layouts/PrivateLayout'
import { useNavigate } from 'react-router-dom'
import useDecodedToken from '../../utils/useDecodedToken'
import SendDuoInvitationModal from '../../components/modals/SendDuoInvitationModal'
import {
  fetchSoloDuoInvitations,
  fetchSentSoloDuoInvitations,
  cancelSoloDuoInvitation,
  acceptSoloDuoInvitation,
  declineSoloDuoInvitation,
  fetchUserCharacters,
} from '../../api/api'

const TOAST_DURATION = 4000

/** WoW class colors (official game palette) for character name styling. */
const WOW_CLASS_COLORS = {
  'death knight': '#C41E3A',
  'demon hunter': '#A330C9',
  druid: '#FF7C0A',
  evoker: '#33937F',
  hunter: '#AAD372',
  mage: '#3FC7EB',
  monk: '#00FF98',
  paladin: '#F48CBA',
  priest: '#FFFFFF',
  rogue: '#FFF468',
  shaman: '#0070DD',
  warlock: '#8788EE',
  warrior: '#C69B6D'
}

function getClassColor (className, lightBackground = false) {
  if (!className || typeof className !== 'string') return null
  const key = className.toLowerCase().trim()
  const color = WOW_CLASS_COLORS[key] || null
  if (!color || !lightBackground) return color
  if (key === 'priest') return '#9B9B9B'
  return color
}

/** Build WoW character avatar URL from Blizzard render CDN (realm slug + name + region). */
function getCharacterAvatarUrl (char) {
  if (!char?.characterName) return null
  const region = (char.region || 'eu').toLowerCase()
  const realmSlug = (char.realm || '').toLowerCase().replace(/\s+/g, '-').replace(/'/g, '')
  const nameSlug = char.characterName.toLowerCase().replace(/\s+/g, '-').replace(/'/g, '')
  if (!realmSlug || !nameSlug) return null
  return `https://render.worldofwarcraft.com/character/${region}/${realmSlug}/${nameSlug}-main.jpg`
}

/** Get avatar URL: prefer API mediaLinks, then Blizzard render URL. */
function getInvitationCharacterAvatar (char) {
  const fromApi = char?.mediaLinks?.[0]?.value
  if (fromApi) return fromApi
  return getCharacterAvatarUrl(char)
}

/** Full character line: name, realm, class, level, faction (no BattleTag). */
function getCharacterDetailLine (char) {
  if (!char) return ''
  const name = char.characterName
  const realm = char.realm
  const region = char.region ? char.region.toUpperCase() : ''
  const cls = char.characterClass
  const level = char.level
  const faction = char.faction
  const parts = [name, realm, region].filter(Boolean)
  const main = parts.join(' — ')
  const extra = [cls, level ? `Lv.${level}` : '', faction].filter(Boolean)
  return extra.length ? `${main} · ${extra.join(' · ')}` : main
}

function getReceivedInvitationLabel (inv) {
  const char = inv.inviterCharacter
  return getCharacterDetailLine(char) || inv.id
}

/** Build a character-like object from sent-invitation fields (API may use different shapes). */
function getSentInvitationInviteeCharacter (inv) {
  const char = inv.inviteeCharacter || inv.invitee || inv.recipientCharacter || inv.toCharacter
  if (char && (char.characterName || char.name)) {
    return {
      characterName: char.characterName ?? char.name,
      realm: char.realm ?? inv.toCharacterRealm,
      region: char.region ?? inv.toCharacterRegion,
      characterClass: char.characterClass ?? char.class,
      level: char.level,
      faction: char.faction
    }
  }
  if (inv.toCharacterName || inv.toCharacterRealm) {
    return {
      characterName: inv.toCharacterName,
      realm: inv.toCharacterRealm,
      region: inv.toCharacterRegion,
      characterClass: inv.toCharacterClass,
      level: inv.toCharacterLevel,
      faction: inv.toCharacterFaction
    }
  }
  return null
}

function getSentInvitationLabel (inv) {
  const char = getSentInvitationInviteeCharacter(inv)
  if (char?.characterName) return getCharacterDetailLine(char)
  const name = inv.toCharacterName
  const realm = inv.toCharacterRealm
  if (name) return [name, realm].filter(Boolean).join(' — ')
  if (inv.toUser?.battletag) return inv.toUser.battletag
  return inv.id
}

function getReceivedInvitationSubtitle (inv) {
  const char = inv.inviterCharacter
  if (!char?.realm && !char?.region) return null
  return [char.realm, char.region ? char.region.toUpperCase() : ''].filter(Boolean).join(' · ')
}

function InvitationCharacterBlock ({ character, accentClass = 'text-purple-300', showAvatar = true, lightBackground = false }) {
  const [avatarFailed, setAvatarFailed] = React.useState(false)
  const avatarUrl = showAvatar ? getInvitationCharacterAvatar(character) : null
  const showImg = avatarUrl && !avatarFailed
  const detailLine = getCharacterDetailLine(character)
  const classColor = getClassColor(character?.characterClass, lightBackground)
  const nameStyle = classColor ? { color: classColor } : undefined
  if (!detailLine) return <span className={accentClass}>—</span>
  return (
    <div className="flex items-center gap-3">
      {showAvatar && (
        <div className="flex-shrink-0 w-10 h-10 rounded-full overflow-hidden border-2 border-white/20 bg-white/5 flex items-center justify-center">
          {showImg ? (
            <img
              src={avatarUrl}
              alt={character?.characterName || ''}
              className="w-full h-full object-cover"
              onError={() => setAvatarFailed(true)}
            />
          ) : (
            <i className="fas fa-user text-gray-500" aria-hidden></i>
          )}
        </div>
      )}
      <div className="flex flex-col gap-0.5 min-w-0">
        <span
          className={`text-sm font-semibold truncate ${!classColor ? accentClass : ''}`}
          style={nameStyle}
        >
          {character?.characterName ?? '—'}
        </span>
        <span className="text-xs text-white">
          {[character?.realm, character?.region ? character.region.toUpperCase() : ''].filter(Boolean).join(' · ')}
          {[character?.characterClass, character?.level ? `Lv.${character.level}` : '', character?.faction].filter(Boolean).length > 0 && (
            <span className="ml-1">· {[character?.characterClass, character?.level ? `Lv.${character.level}` : '', character?.faction].filter(Boolean).join(' · ')}</span>
          )}
        </span>
      </div>
    </div>
  )
}

function Toast ({ toasts }) {
  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-2xl text-sm font-medium text-white transition-all ${
            t.type === 'error' ? 'bg-red-600' : 'bg-emerald-600'
          }`}
        >
          <i className={`fas ${t.type === 'error' ? 'fa-exclamation-circle' : 'fa-check-circle'} text-base`}></i>
          {t.message}
        </div>
      ))}
    </div>
  )
}

function SoloDuoInvitations () {
  const backgroundImage = '/ww-wallpaper.webp'
  const { token } = useDecodedToken()
  const navigate = useNavigate()

  const [receivedInvitations, setReceivedInvitations] = useState([])
  const [sentInvitations, setSentInvitations] = useState([])
  const [isLoadingReceived, setIsLoadingReceived] = useState(false)
  const [isLoadingSent, setIsLoadingSent] = useState(false)
  const [toasts, setToasts] = useState([])
  const [actionLoadingId, setActionLoadingId] = useState(null)

  const [acceptModalOpen, setAcceptModalOpen] = useState(false)
  const [acceptingInvitation, setAcceptingInvitation] = useState(null)
  const [acceptCharacterId, setAcceptCharacterId] = useState('')
  const [acceptError, setAcceptError] = useState('')
  const [isLoadingAcceptModal, setIsLoadingAcceptModal] = useState(false)
  const [acceptCharacters, setAcceptCharacters] = useState([])

  const [sendModalOpen, setSendModalOpen] = useState(false)

  const addToast = useCallback((message, type = 'success') => {
    const id = Date.now()
    setToasts((prev) => [...prev, { id, message, type }])
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
    }, TOAST_DURATION)
  }, [])

  const loadReceivedInvitations = useCallback(async () => {
    if (!token) return
    setIsLoadingReceived(true)
    try {
      const data = await fetchSoloDuoInvitations(token)
      console.log('[Duo Invitations] Received invitations:', data)
      setReceivedInvitations(Array.isArray(data) ? data : data?.invitations || [])
    } catch (err) {
      console.error('[Duo Invitations] Failed to fetch received invitations:', err)
      addToast(err.message || 'Failed to load received invitations.', 'error')
    } finally {
      setIsLoadingReceived(false)
    }
  }, [token, addToast])

  const loadSentInvitations = useCallback(async () => {
    if (!token) return
    setIsLoadingSent(true)
    try {
      const data = await fetchSentSoloDuoInvitations(token)
      console.log('[Duo Invitations] Sent invitations:', data)
      setSentInvitations(Array.isArray(data) ? data : data?.invitations || [])
    } catch (err) {
      console.error('[Duo Invitations] Failed to fetch sent invitations:', err)
      addToast(err.message || 'Failed to load sent invitations.', 'error')
    } finally {
      setIsLoadingSent(false)
    }
  }, [token, addToast])

  useEffect(() => {
    loadReceivedInvitations()
    loadSentInvitations()
  }, [loadReceivedInvitations, loadSentInvitations])

  const handleCancelInvitation = useCallback(async (id) => {
    if (!token) return
    setActionLoadingId(id)
    setSentInvitations((prev) => prev.filter((inv) => inv.id !== id))
    try {
      const data = await cancelSoloDuoInvitation(token, id)
      console.log('[Duo Invitations] Cancelled invitation:', id, data)
      addToast('Invitation cancelled successfully.')
    } catch (err) {
      console.error('[Duo Invitations] Failed to cancel invitation:', id, err)
      const fresh = await fetchSentSoloDuoInvitations(token).catch(() => null)
      const stillExists = fresh && (Array.isArray(fresh) ? fresh : fresh?.invitations || []).some((inv) => inv.id === id)
      if (stillExists) {
        setSentInvitations(Array.isArray(fresh) ? fresh : fresh?.invitations || [])
        addToast('Failed to cancel invitation. Please try again.', 'error')
      }
    } finally {
      setActionLoadingId(null)
    }
  }, [token, addToast])

  const handleDeclineInvitation = useCallback(async (id) => {
    if (!token) return
    setActionLoadingId(id)
    setReceivedInvitations((prev) => prev.filter((inv) => inv.id !== id))
    try {
      const data = await declineSoloDuoInvitation(token, id)
      console.log('[Duo Invitations] Declined invitation:', id, data)
      addToast('Invitation declined.')
    } catch (err) {
      console.error('[Duo Invitations] Failed to decline invitation:', id, err)
      const fresh = await fetchSoloDuoInvitations(token).catch(() => null)
      const stillExists = fresh && (Array.isArray(fresh) ? fresh : fresh?.invitations || []).some((inv) => inv.id === id)
      if (stillExists) {
        setReceivedInvitations(Array.isArray(fresh) ? fresh : fresh?.invitations || [])
        addToast('Failed to decline invitation. Please try again.', 'error')
      }
    } finally {
      setActionLoadingId(null)
    }
  }, [token, addToast])

  const openAcceptModal = useCallback(async (invitation) => {
    setAcceptingInvitation(invitation)
    setAcceptCharacterId('')
    setAcceptError('')
    setAcceptCharacters([])
    setAcceptModalOpen(true)
    setIsLoadingAcceptModal(true)
    try {
      const charsData = await fetchUserCharacters(token)
      const chars = Array.isArray(charsData) ? charsData : charsData?.characters || charsData?.data || []
      setAcceptCharacters(chars)
      if (chars.length > 0) {
        setAcceptCharacterId(String(chars[0].characterId || chars[0].id || ''))
      }
    } catch (err) {
      console.error('[Duo Invitations] Failed to load characters for accept modal:', err)
    } finally {
      setIsLoadingAcceptModal(false)
    }
  }, [token])

  const handleAcceptInvitation = useCallback(async () => {
    if (!token || !acceptingInvitation) return
    if (!acceptCharacterId.trim()) {
      setAcceptError('Please select a character to accept with.')
      return
    }
    setAcceptError('')
    setActionLoadingId(acceptingInvitation.id)
    try {
      const data = await acceptSoloDuoInvitation(token, acceptingInvitation.id, acceptCharacterId.trim())
      console.log('[Duo Invitations] Accepted invitation:', acceptingInvitation.id, data)
      addToast('Invitation accepted! You are now registered as a duo.')
      setAcceptModalOpen(false)
      setAcceptingInvitation(null)
      await loadReceivedInvitations()
    } catch (err) {
      console.error('[Duo Invitations] Failed to accept invitation:', acceptingInvitation.id, err)
      setAcceptError('Failed to accept invitation. Please try again.')
    } finally {
      setActionLoadingId(null)
    }
  }, [token, acceptingInvitation, acceptCharacterId, addToast, loadReceivedInvitations])

  const handleRefreshAll = useCallback(async () => {
    await Promise.all([loadReceivedInvitations(), loadSentInvitations()])
    addToast('Invitations refreshed.')
  }, [loadReceivedInvitations, loadSentInvitations, addToast])

  const isLoading = isLoadingReceived || isLoadingSent

  return (
    <PrivateLayout backgroundImage={backgroundImage}>
      <div className="flex flex-col items-center min-h-[calc(100vh-4rem)] text-white bg-black/50 px-4 py-10">
        <div className="w-full max-w-4xl">

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-5xl font-bold drop-shadow-lg mb-2">
              Duo Invitations
            </h1>
            <p className="text-gray-300 text-base md:text-lg">
              Manage your incoming and outgoing duo ladder invitations
            </p>
          </div>

          {/* Top action bar */}
          <div className="flex justify-between items-center mb-6 gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => navigate('/solo-duo-ranking/standings')}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-white/10 hover:bg-white/20 transition-colors"
            >
              <i className="fas fa-arrow-left text-xs"></i>
              Back to Standings
            </button>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setSendModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-purple-600 hover:bg-purple-500 transition-colors shadow-lg"
              >
                <i className="fas fa-paper-plane text-xs"></i>
                Send Invitation
              </button>
              <button
                type="button"
                onClick={handleRefreshAll}
                disabled={isLoading}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-blue-600 hover:bg-blue-500 transition-colors disabled:opacity-50"
              >
                <i className={`fas fa-sync-alt text-xs ${isLoading ? 'animate-spin' : ''}`}></i>
                Refresh
              </button>
            </div>
          </div>

          {/* Received invitations */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <h2 className="text-xl font-bold text-white">Received Invitations</h2>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-600/40 text-purple-200 border border-purple-500/30">
                {receivedInvitations.filter((inv) => inv.status === 'pending').length}
              </span>
            </div>

            {isLoadingReceived && (
              <div className="flex items-center gap-3 py-8 justify-center">
                <div className="w-6 h-6 border-2 border-purple-400 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-gray-400 text-sm">Loading received invitations...</span>
              </div>
            )}

            {!isLoadingReceived && receivedInvitations.filter((inv) => inv.status === 'pending').length === 0 && (
              <div className="rounded-xl border border-white/10 bg-white/5 px-6 py-10 text-center">
                <i className="fas fa-envelope-open text-3xl text-gray-600 mb-3 block"></i>
                <p className="text-gray-400">No pending invitations received.</p>
              </div>
            )}

            {!isLoadingReceived && receivedInvitations.filter((inv) => inv.status === 'pending').length > 0 && (
              <div className="rounded-xl border border-white/10 overflow-hidden shadow-xl divide-y divide-white/5">
                {receivedInvitations.filter((inv) => inv.status === 'pending').map((inv) => (
                  <div key={inv.id} className="flex items-center justify-between px-5 py-4 bg-white/5 hover:bg-white/10 transition-colors gap-4 flex-wrap">
                    <div className="flex flex-col gap-0.5">
                      <span className="text-sm font-semibold text-white mb-1">From:</span>
                      {inv.inviterCharacter ? (
                        <InvitationCharacterBlock character={inv.inviterCharacter} accentClass="text-purple-300" showAvatar />
                      ) : (
                        <span className="text-purple-300">{getReceivedInvitationLabel(inv)}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => openAcceptModal(inv)}
                        disabled={actionLoadingId === inv.id}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-600 hover:bg-emerald-500 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {actionLoadingId === inv.id
                          ? <i className="fas fa-spinner fa-spin"></i>
                          : <i className="fas fa-check"></i>
                        }
                        Accept
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeclineInvitation(inv.id)}
                        disabled={actionLoadingId === inv.id}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-red-600 hover:bg-red-500 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {actionLoadingId === inv.id
                          ? <i className="fas fa-spinner fa-spin"></i>
                          : <i className="fas fa-times"></i>
                        }
                        Decline
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Sent invitations */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <h2 className="text-xl font-bold text-white">Sent Invitations</h2>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-600/40 text-blue-200 border border-blue-500/30">
                {sentInvitations.filter((inv) => inv.status === 'pending').length}
              </span>
            </div>

            {isLoadingSent && (
              <div className="flex items-center gap-3 py-8 justify-center">
                <div className="w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-gray-400 text-sm">Loading sent invitations...</span>
              </div>
            )}

            {!isLoadingSent && sentInvitations.filter((inv) => inv.status === 'pending').length === 0 && (
              <div className="rounded-xl border border-white/10 bg-white/5 px-6 py-10 text-center">
                <i className="fas fa-paper-plane text-3xl text-gray-600 mb-3 block"></i>
                <p className="text-gray-400">No pending invitations sent.</p>
              </div>
            )}

            {!isLoadingSent && sentInvitations.filter((inv) => inv.status === 'pending').length > 0 && (
              <div className="rounded-xl border border-white/10 overflow-hidden shadow-xl divide-y divide-white/5">
                {sentInvitations.filter((inv) => inv.status === 'pending').map((inv) => {
                  const inviteeChar = inv.inviteeCharacter || getSentInvitationInviteeCharacter(inv)
                  return (
                  <div key={inv.id} className="flex items-center justify-between px-5 py-4 bg-white/5 hover:bg-white/10 transition-colors gap-4 flex-wrap">
                    <div className="flex flex-col gap-0.5">
                      <span className="text-sm font-semibold text-white mb-1">To:</span>
                      {inviteeChar ? (
                        <InvitationCharacterBlock character={inviteeChar} accentClass="text-blue-300" showAvatar />
                      ) : (
                        <span className="text-blue-300">{getSentInvitationLabel(inv)}</span>
                      )}
                      {(inv.inviterCharacter?.characterName || inv.myCharacterName) && (
                        <span className="text-xs text-white mt-1">Your character: {inv.inviterCharacter?.characterName || inv.myCharacterName}</span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleCancelInvitation(inv.id)}
                      disabled={actionLoadingId === inv.id}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-red-600 hover:bg-red-500 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {actionLoadingId === inv.id
                        ? <i className="fas fa-spinner fa-spin"></i>
                        : <i className="fas fa-times"></i>
                      }
                      Cancel
                    </button>
                  </div>
                  ); })}
              </div>
            )}
          </section>
        </div>
      </div>

      <SendDuoInvitationModal
        token={token}
        isOpen={sendModalOpen}
        onClose={() => setSendModalOpen(false)}
        onSent={async (label) => {
          addToast(`Invitation sent to ${label}!`)
          await Promise.all([loadSentInvitations(), loadReceivedInvitations()])
        }}
      />

      {/* Accept invitation modal */}
      {acceptModalOpen && acceptingInvitation && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/80 z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 text-gray-900">
            <h2 className="text-xl font-bold mb-2 text-center">Accept Duo Invitation</h2>
            <p className="text-sm text-gray-500 text-center mb-5">
              Accepting invitation from
            </p>
            {acceptingInvitation?.inviterCharacter ? (
              <div className="flex justify-center mb-5">
                <InvitationCharacterBlock
                  character={acceptingInvitation.inviterCharacter}
                  accentClass="text-gray-800"
                  showAvatar
                  lightBackground
                />
              </div>
            ) : (
              <p className="text-center mb-5 font-semibold text-gray-700">
                {getReceivedInvitationLabel(acceptingInvitation)}
              </p>
            )}

            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                Your character <span className="text-red-500">*</span>
              </label>
              {isLoadingAcceptModal ? (
                <div className="flex items-center gap-2 text-gray-400 text-sm py-2">
                  <div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                  Loading characters...
                </div>
              ) : acceptCharacters.length > 0 ? (
                <select
                  value={acceptCharacterId}
                  onChange={(e) => { setAcceptCharacterId(e.target.value); setAcceptError('') }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  {acceptCharacters.map((c) => {
                    const id = String(c.characterId || c.id || '')
                    const realmName = c.realm?.name || c.realm || ''
                    const label = [c.name, realmName].filter(Boolean).join(' — ') || id
                    return <option key={id} value={id}>{label}</option>
                  })}
                </select>
              ) : (
                <input
                  type="text"
                  value={acceptCharacterId}
                  onChange={(e) => { setAcceptCharacterId(e.target.value); setAcceptError('') }}
                  placeholder="Enter your character ID"
                  className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 ${acceptError ? 'border-red-400' : 'border-gray-300'}`}
                />
              )}
              {acceptError && (
                <p className="text-xs text-red-500 mt-1.5">{acceptError}</p>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => { setAcceptModalOpen(false); setAcceptingInvitation(null); setAcceptError('') }}
                className="px-4 py-2 rounded-full text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAcceptInvitation}
                disabled={actionLoadingId === acceptingInvitation.id || isLoadingAcceptModal}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoadingId === acceptingInvitation.id
                  ? <i className="fas fa-spinner fa-spin"></i>
                  : <i className="fas fa-check"></i>
                }
                Accept
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast toasts={toasts} />
    </PrivateLayout>
  )
}

export default SoloDuoInvitations
